/* eslint-disable no-param-reassign */
import { prefersReducedMotion, hasFinePointer } from './motion-utils'

/**
 * v-tilt — 3D parallax tilt + lift on hover (the Play "tilt card" from the
 * mockups). The card rotates toward the cursor and rises on a deeper hard
 * shadow, then settles on leave. Add
 * `transition: transform var(--dur-fast) var(--ease-out), box-shadow …`
 * on the element so the release eases.
 *
 * Usage:
 *   <PlayCard v-tilt>…</PlayCard>
 *   <PlayCard v-tilt="{ max: 6, lift: 5 }">…</PlayCard>
 *
 * Binding value (optional):
 *   max  — max rotation in deg per axis (default 6, from the mockup)
 *   lift — upward translate in px (default 5)
 *
 * No-ops on touch and when the user prefers reduced motion.
 *
 * VUE3-MIGRATION: rename `bind`→`mounted`, `unbind`→`unmounted`. Bodies stay.
 */

const STORE = '__playTilt__'

function optsFromBinding(binding) {
  const v = binding.value || {}
  return {
    max: typeof v.max === 'number' ? v.max : 6,
    lift: typeof v.lift === 'number' ? v.lift : 5,
  }
}

export const tilt = {
  bind(el, binding) {
    if (prefersReducedMotion() || !hasFinePointer()) return

    const onMove = e => {
      const { max, lift } = optsFromBinding(binding)
      const r = el.getBoundingClientRect()
      const px = (e.clientX - r.left) / r.width - 0.5
      const py = (e.clientY - r.top) / r.height - 0.5
      el.style.transform = `perspective(800px) rotateY(${px * max}deg) rotateX(${-py * max}deg) translateY(${-lift}px)`
      el.style.boxShadow = '0 14px 0 var(--border-ink)'
    }
    const onLeave = () => {
      el.style.transform = ''
      el.style.boxShadow = ''
    }

    el.addEventListener('mousemove', onMove)
    el.addEventListener('mouseleave', onLeave)
    el[STORE] = { onMove, onLeave }
  },

  unbind(el) {
    const handlers = el[STORE]
    if (!handlers) return
    el.removeEventListener('mousemove', handlers.onMove)
    el.removeEventListener('mouseleave', handlers.onLeave)
    el.style.transform = ''
    el.style.boxShadow = ''
    delete el[STORE]
  },
}

export default tilt
